import { useState } from 'react'
import { Panel, SectionHeader, ErpTable, Th, Td, EmptyRow } from '@/components/erp/primitives'
import { ErrorStrip, Forbidden, LoadingBlock, Note } from '@/shared/ui/states'
import { useCan } from '@/shared/auth/session'
import { useLoad } from '@/features/administration/hooks'
import { explain } from '@/shared/api/problem'
import { fmtDate } from '@/shared/lib/format'
import { advanceElection, listElections } from './api'
import { ElectionFormDialog } from './ElectionFormDialog'

/**
 * Elections — the platform's shared reference data.
 *
 * An election is not owned by any one module: `turnout` organises cells for one, and
 * `survey` and `politics` will reference the same rows. So it lives at the top level and
 * every other module points at it by id.
 */
export function ElectionsPage() {
  const canRead = useCan('election:read')
  const canManage = useCan('election:manage')
  const [creating, setCreating] = useState(false)
  const [advancing, setAdvancing] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const elections = useLoad(() => listElections(), [])

  if (!canRead) return <Forbidden what="elections" />

  const rows = elections.data ?? []

  const advance = async (id: string) => {
    setAdvancing(id)
    setError(null)
    try {
      await advanceElection(id)
      elections.reload()
    } catch (e) {
      setError(explain(e))
    } finally {
      setAdvancing(null)
    }
  }

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Elections"
        description="Every module that works towards a polling day is scoped to one of these."
        right={(
          /* Offered even without election:manage — the server's refusal is what speaks.
             A hidden control cannot be told apart from a broken one. */
          <button type="button" className="erp-btn erp-btn-primary h-9"
            onClick={() => setCreating(true)}>
            New election
          </button>
        )}
      />

      <ErrorStrip message={error ?? elections.error} />

      {elections.loading && rows.length === 0 ? (
        <LoadingBlock label="Loading elections…" />
      ) : (
        <Panel
          title={`${rows.length} election${rows.length === 1 ? '' : 's'}`}
          description="Status is the state machine position, not a label. There are no backward transitions."
        >
          <ErpTable head={(
            <>
              <Th>Code</Th>
              <Th>Name</Th>
              <Th>Type</Th>
              <Th>Polling date</Th>
              <Th>Scope</Th>
              <Th>Status</Th>
              <Th className="text-right">Advance</Th>
            </>
          )}>
            {rows.length === 0 ? (
              <EmptyRow colSpan={7}
                message="No elections yet. Create one and the turnout, polling and candidate modules can be scoped to it." />
            ) : rows.map((e) => (
              <tr key={e.id} className="hover:bg-muted/40">
                <Td className="font-mono text-erp-xs">{e.code}</Td>
                <Td>{e.name}</Td>
                <Td className="text-muted-foreground">{e.electionType}</Td>
                <Td className="text-muted-foreground">{fmtDate(e.pollingDate)}</Td>
                <Td className="text-muted-foreground">
                  {e.unitPath ? e.unitPath : 'National'}
                </Td>
                <Td>{e.statusCode}</Td>
                <Td className="text-right">
                  {e.statusCode === 'RECONCILED' ? (
                    <span className="text-erp-xs text-muted-foreground">final</span>
                  ) : (
                    <button type="button" className="erp-btn-ghost h-8 text-erp-xs"
                      disabled={advancing === e.id || !canManage}
                      onClick={() => { void advance(e.id) }}>
                      {advancing === e.id ? 'Advancing…' : nextPhaseOf(e.statusCode)}
                    </button>
                  )}
                </Td>
              </tr>
            ))}
          </ErpTable>

          <div className="px-4 pb-3 pt-1">
            <Note>
              <strong>PLANNED → ORGANISING → ENGAGING → POLLING → CLOSED → RECONCILED.</strong>{' '}
              Advancing to CLOSED is what writes each cell member's turnout back into the
              voter register, so it is not reversible.
            </Note>
          </div>
        </Panel>
      )}

      <ElectionFormDialog
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={() => elections.reload()}
      />
    </div>
  )
}

/** The label names where the button GOES, which is the question somebody clicking it has. */
function nextPhaseOf(status: string): string {
  const next: Record<string, string> = {
    PLANNED: '→ Organising',
    ORGANISING: '→ Engaging',
    ENGAGING: '→ Polling',
    POLLING: '→ Close',
    CLOSED: '→ Reconcile',
  }
  return next[status] ?? 'Advance'
}
